import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const ModernHeader = ({ scrollY, alwaysOpaque = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isScrolled = alwaysOpaque || scrollY > 50;

  // Obtener usuario del localStorage
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('plaza_user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      return null;
    }
  });

  // Escuchar cambios en autenticación
  useEffect(() => {
    const handleAuthChange = () => {
      try {
        const storedUser = localStorage.getItem('plaza_user');
        setUser(storedUser ? JSON.parse(storedUser) : null);
      } catch (e) {
        setUser(null);
      }
    };

    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('plaza_user');
    setUser(null);
    window.dispatchEvent(new Event('auth-change'));
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
        ? 'bg-white/90 backdrop-blur-lg shadow-lg py-3'
        : 'bg-transparent py-4'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo - Blanco sin scroll, Verde con scroll */}
          <div className="flex items-center space-x-3 group cursor-pointer">
            <a href="/web/">
              {isScrolled ? (
                <img
                  src="/web/logo.png"
                  alt="Plaza Rural"
                  className="h-12 w-auto transform group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <img
                  src="/web/logo_blanco.png"
                  alt="Plaza Rural"
                  className="h-12 w-auto transform group-hover:scale-105 transition-transform duration-300 drop-shadow-2xl"
                />
              )}
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {[
              { label: 'Inicio', path: '/' },
              { label: 'Remates', path: '/remates' },
              { label: 'Precios', path: '/precios' },
              { label: 'Noticias', path: '/noticias' },
              { label: 'Contacto', path: '/contacto' }
            ].map((item, index) => (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className={`relative text-sm font-semibold transition-colors duration-300 group ${isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                  }`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm">
              <Phone className={`w-4 h-4 ${isScrolled ? 'text-green-600' : 'text-white'}`} />
              <span className={`font-semibold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                26041258
              </span>
            </div>

            {/* Botón de Ingreso Privado Admin */}
            <a
              href="https://backend.plazarural.com.uy"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300 hover:bg-white/10 ${isScrolled
                ? 'border-gray-300 text-gray-700'
                : 'border-white/30 text-white'
                }`}
            >
              <Lock className="w-4 h-4" />
              <span className="text-sm font-medium">Admin</span>
            </a>

            {user ? (
              <div className="flex items-center gap-4">
                <span className={`text-sm font-medium ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                  Hola, {user.nombre}
                </span>
                <Button
                  onClick={handleLogout}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Salir
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Ingresar
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {mobileMenuOpen ? (
              <X className={isScrolled ? 'text-gray-900' : 'text-white'} />
            ) : (
              <Menu className={isScrolled ? 'text-gray-900' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 p-4 bg-white rounded-2xl shadow-xl animate-fade-in-up">
            <nav className="flex flex-col space-y-3">
              {[
                { label: 'Inicio', path: '/' },
                { label: 'Remates', path: '/remates' },
                { label: 'Precios', path: '/precios' },
                { label: 'Noticias', path: '/noticias' },
                { label: 'Contacto', path: '/contacto' }
              ].map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="text-gray-700 hover:text-green-600 font-semibold py-2 transition-colors text-left"
                >
                  {item.label}
                </button>
              ))}

              <a
                href="https://backend.plazarural.com.uy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-700 font-semibold py-2 hover:text-green-600"
              >
                <Lock className="w-4 h-4" />
                Admin
              </a>

              {user ? (
                <>
                  <div className="py-2 text-gray-700 font-medium border-t border-gray-100 mt-2">
                    Hola, {user.nombre}
                  </div>
                  <Button
                    onClick={handleLogout}
                    className="w-full bg-red-500 hover:bg-red-600 text-white"
                  >
                    Cerrar Sesión
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Ingresar
                </Button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default ModernHeader;